import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
    ReportDefinition,
    ReportExportRun,
    ReportTemplate,
    ReportTemplateBindingCatalog,
    ReportTemplateVersion,
    ReportPreviewResponse,
    ReportingDataset,
    SaveTemplateRequest,
    SaveSqlReportQueryRequest,
    SqlReportExportRequest,
    SqlReportPreviewRequest,
    SqlReportPreviewResult,
    SqlReportQuery,
    SqlReportSource,
    SqlReportValidation,
    TemplatePreviewRequest,
    TemplatePreviewResponse
} from './reporting.models';

@Injectable({
    providedIn: 'root'
})
export class ReportingService {
    private readonly baseUrl = `${environment.restApiUrl}reporting`;

    constructor(private readonly http: HttpClient) {}

    getDatasets(): Observable<ReportingDataset[]> {
        return this.http.get<ReportingDataset[]>(`${this.baseUrl}/datasets`);
    }

    getDatasetSchema(datasetKey: string): Observable<ReportingDataset> {
        return this.http.get<ReportingDataset>(`${this.baseUrl}/datasets/${datasetKey}/schema`);
    }

    getReports(companyId: number): Observable<ReportDefinition[]> {
        const params = new HttpParams().set('companyId', String(companyId));
        return this.http.get<ReportDefinition[]>(`${this.baseUrl}/reports`, { params });
    }

    saveReport(definition: ReportDefinition): Observable<ReportDefinition> {
        if (definition.reportId) {
            return this.http.put<ReportDefinition>(`${this.baseUrl}/reports/${definition.reportId}`, definition);
        }
        return this.http.post<ReportDefinition>(`${this.baseUrl}/reports`, definition);
    }

    previewDefinition(definition: ReportDefinition): Observable<ReportPreviewResponse> {
        return this.http.post<ReportPreviewResponse>(`${this.baseUrl}/reports/preview`, definition);
    }

    previewReport(reportId: number): Observable<ReportPreviewResponse> {
        return this.http.post<ReportPreviewResponse>(`${this.baseUrl}/reports/${reportId}/preview`, {});
    }

    exportReport(reportId: number, format: string): Observable<ReportExportRun> {
        const params = new HttpParams().set('format', format);
        return this.http.post<ReportExportRun>(`${this.baseUrl}/reports/${reportId}/exports`, {}, { params });
    }

    getTemplates(companyId: number, reportType?: string): Observable<ReportTemplate[]> {
        let params = new HttpParams().set('companyId', String(companyId));
        if (reportType) {
            params = params.set('reportType', reportType);
        }
        return this.http.get<ReportTemplate[]>(`${this.baseUrl}/templates`, { params });
    }

    saveTemplate(template: SaveTemplateRequest, templateId?: number): Observable<ReportTemplate> {
        if (templateId) {
            return this.http.put<ReportTemplate>(`${this.baseUrl}/templates/${templateId}`, template);
        }
        return this.http.post<ReportTemplate>(`${this.baseUrl}/templates`, template);
    }

    getTemplateVersions(templateId: number): Observable<ReportTemplateVersion[]> {
        return this.http.get<ReportTemplateVersion[]>(`${this.baseUrl}/templates/${templateId}/versions`);
    }

    restoreTemplateVersion(templateId: number, versionId: number): Observable<ReportTemplate> {
        return this.http.post<ReportTemplate>(`${this.baseUrl}/templates/${templateId}/versions/${versionId}/restore`, {});
    }

    getTemplateBindings(reportType: string): Observable<ReportTemplateBindingCatalog> {
        return this.http.get<ReportTemplateBindingCatalog>(`${this.baseUrl}/templates/bindings/${reportType}`);
    }

    previewTemplate(request: TemplatePreviewRequest): Observable<TemplatePreviewResponse> {
        return this.http.post<TemplatePreviewResponse>(`${this.baseUrl}/templates/preview`, request);
    }

    validateTemplate(templateId: number, versionId?: number): Observable<TemplatePreviewResponse> {
        let params = new HttpParams();
        if (versionId) {
            params = params.set('versionId', String(versionId));
        }
        return this.http.post<TemplatePreviewResponse>(`${this.baseUrl}/templates/${templateId}/validate`, {}, { params });
    }

    publishTemplate(templateId: number, versionId?: number): Observable<ReportTemplate> {
        let params = new HttpParams();
        if (versionId) {
            params = params.set('versionId', String(versionId));
        }
        return this.http.post<ReportTemplate>(`${this.baseUrl}/templates/${templateId}/publish`, {}, { params });
    }

    getSqlSources(companyId: number): Observable<SqlReportSource[]> {
        const params = new HttpParams().set('companyId', String(companyId));
        return this.http.get<SqlReportSource[]>(`${this.baseUrl}/sql/sources`, { params });
    }

    getSqlQueries(companyId: number): Observable<SqlReportQuery[]> {
        const params = new HttpParams().set('companyId', String(companyId));
        return this.http.get<SqlReportQuery[]>(`${this.baseUrl}/sql/queries`, { params });
    }

    saveSqlQuery(request: SaveSqlReportQueryRequest, queryId?: number): Observable<SqlReportQuery> {
        if (queryId) {
            return this.http.put<SqlReportQuery>(`${this.baseUrl}/sql/queries/${queryId}`, request);
        }
        return this.http.post<SqlReportQuery>(`${this.baseUrl}/sql/queries`, request);
    }

    publishSqlQuery(queryId: number): Observable<SqlReportQuery> {
        return this.http.post<SqlReportQuery>(`${this.baseUrl}/sql/queries/${queryId}/publish`, {});
    }

    unpublishSqlQuery(queryId: number): Observable<SqlReportQuery> {
        return this.http.post<SqlReportQuery>(`${this.baseUrl}/sql/queries/${queryId}/unpublish`, {});
    }

    validateSqlQuery(request: SqlReportPreviewRequest): Observable<SqlReportValidation> {
        return this.http.post<SqlReportValidation>(`${this.baseUrl}/sql/validate`, request);
    }

    previewSqlQuery(request: SqlReportPreviewRequest): Observable<SqlReportPreviewResult> {
        return this.http.post<SqlReportPreviewResult>(`${this.baseUrl}/sql/preview`, request);
    }

    exportSqlQuery(request: SqlReportExportRequest): Observable<ReportExportRun> {
        return this.http.post<ReportExportRun>(`${this.baseUrl}/sql/exports`, request);
    }
}
